import { TimeBucket_TimeSeries } from '../proto/generated/common'
import {
    AlertExpression_Type,
    Condition,
    Condition_CompositeCondition_Operator,
    Condition_SecurityAlertCondition,
    Condition_SecurityAlertCondition_Operator
} from '../proto/generated/nexus'
import { getPrimarySecurityAlertCondition } from './alerts'

/** Fallback when parsing yields no security row (matches shape used in createUserAlertFromSnapshot). */
const emptySecurityAlertCondition: Condition_SecurityAlertCondition = {
    operator: Condition_SecurityAlertCondition_Operator.EQUAL_TO,
    lhsExpression: {
        type: AlertExpression_Type.SIMPLE_SECURITY,
        timeBucket: { timeSeries: TimeBucket_TimeSeries.SECOND, value: 1 },
        simpleSecurity: { security: undefined, source: 'close' }
    },
    rhsExpression: {
        type: AlertExpression_Type.FIXED_VALUE,
        fixedValue: 0,
        timeBucket: undefined
    }
}

const defaultOp = () => Condition_CompositeCondition_Operator.AND

/** Structural template used for hidden branch headers (split / nested pair); must round-trip through proto. */
function isPlaceholderHeaderSecurity(s: Condition_SecurityAlertCondition): boolean {
    const e = emptySecurityAlertCondition
    return (
        s.operator === e.operator &&
        s.lhsExpression?.type === e.lhsExpression?.type &&
        s.rhsExpression?.type === e.rhsExpression?.type &&
        s.rhsExpression?.fixedValue === e.rhsExpression?.fixedValue &&
        !s.lhsExpression?.simpleSecurity?.security
    )
}

/** Path to the visually first leaf under `node` (always child index 0 at each branch). */
function leftmostLeafPathFrom(prefix: number[], node: CompositeBranchNode): number[] | null {
    if (node.children.length === 0) return prefix
    if (!node.children[0]) return null
    return leftmostLeafPathFrom([...prefix, 0], node.children[0])
}

function flipOp(op: Condition_CompositeCondition_Operator): Condition_CompositeCondition_Operator {
    return op === Condition_CompositeCondition_Operator.OR
        ? Condition_CompositeCondition_Operator.AND
        : Condition_CompositeCondition_Operator.OR
}

export type CompositeBranchNode = {
    security: Condition_SecurityAlertCondition
    parentToGroupOp: Condition_CompositeCondition_Operator
    childrenSiblingOp: Condition_CompositeCondition_Operator
    children: CompositeBranchNode[]
    /**
     * Client-only hints for the composite tree UI (not stored on `Condition` — lost on full proto round-trip).
     */
    compositeUi?: { hideHeadRow?: boolean }
}

export type ConditionForestModel = {
    rootSiblingOp: Condition_CompositeCondition_Operator
    roots: CompositeBranchNode[]
}

export function leafNode(security: Condition_SecurityAlertCondition): CompositeBranchNode {
    return {
        security,
        parentToGroupOp: defaultOp(),
        childrenSiblingOp: defaultOp(),
        children: []
    }
}

/**
 * Multi-root rail split targets a whole root operand. For `hideHeadRow` groups there is no real header row —
 * only peer children — so "split" would carry the structural placeholder security and corrupt the tree.
 * Those groups must be split via the inner row rails (per-leaf paths).
 */
export function showsSplitRailForMultiRootRoot(root: CompositeBranchNode): boolean {
    if (root.children.length === 0) return true
    return root.compositeUi?.hideHeadRow !== true
}

function branchToCondition(n: CompositeBranchNode): Condition {
    if (n.children.length === 0) {
        return { securityAlertCondition: n.security }
    }
    /** No wire payload for the structural placeholder — peers are encoded as a flat composite list. */
    if (n.compositeUi?.hideHeadRow) {
        return {
            compositeCondition: {
                logicalOperator: n.childrenSiblingOp,
                condition: n.children.map(branchToCondition)
            }
        }
    }
    return {
        compositeCondition: {
            logicalOperator: n.parentToGroupOp,
            condition: [
                { securityAlertCondition: n.security },
                {
                    compositeCondition: {
                        logicalOperator: n.childrenSiblingOp,
                        condition: n.children.map(branchToCondition)
                    }
                }
            ]
        }
    }
}

/**
 * Multi-root lists encode each root as a **unary** `compositeCondition` (single child) so the top-level
 * operand list never mixes a multi-child composite next to a bare security (ambiguous with hideHeadRow
 * peer lists and breaks rail split when the first root has `children.length > 0` in the forest).
 */
function wrapRootOperandForMultiRootEncoding(c: Condition): Condition {
    const unary = (inner: Condition) => ({
        compositeCondition: {
            logicalOperator: defaultOp(),
            condition: [inner]
        }
    })
    if (c.securityAlertCondition) {
        return unary(c)
    }
    const comp = c.compositeCondition
    if (comp?.condition?.length === 1) {
        return c
    }
    if (comp?.condition?.length) {
        return unary(c)
    }
    return c
}

/**
 * Unwrap `compositeCondition` nodes whose only operand is a single `securityAlertCondition`
 * (redundant wrapper from encoding or unary groups). Applied bottom-up.
 */
function flattenSingleChildSecurityComposites(cond: Condition): Condition {
    if (cond.securityAlertCondition) {
        return cond
    }
    const comp = cond.compositeCondition
    if (!comp?.condition?.length) {
        return cond
    }
    const nextParts = comp.condition.map((c) => flattenSingleChildSecurityComposites(c))
    if (nextParts.length === 1 && nextParts[0].securityAlertCondition) {
        return { securityAlertCondition: nextParts[0].securityAlertCondition! }
    }
    return {
        compositeCondition: {
            ...comp,
            condition: nextParts
        }
    }
}

export function forestModelToCondition(model: ConditionForestModel): Condition | undefined {
    const { roots, rootSiblingOp } = model
    if (roots.length === 0) return undefined
    if (roots.length === 1) {
        const r = roots[0]
        if (r.children.length === 0) {
            return { securityAlertCondition: r.security }
        }
        return flattenSingleChildSecurityComposites(branchToCondition(r))
    }
    return flattenSingleChildSecurityComposites({
        compositeCondition: {
            logicalOperator: rootSiblingOp,
            condition: roots.map((r) => wrapRootOperandForMultiRootEncoding(branchToCondition(r)))
        }
    })
}

function parseBranchFromCondition(cond: Condition): CompositeBranchNode {
    if (cond.securityAlertCondition) {
        return leafNode(cond.securityAlertCondition)
    }
    const comp = cond.compositeCondition!
    const parts = comp.condition || []
    const op = comp.logicalOperator || defaultOp()

    if (
        parts.length === 2 &&
        parts[0].securityAlertCondition &&
        parts[1].compositeCondition?.condition?.length
    ) {
        const inner = parts[1].compositeCondition!
        const innerParts = inner.condition || []
        const innerAllBareSecurities =
            innerParts.length > 0 && innerParts.every((p) => p.securityAlertCondition)
        /**
         * `branchToCondition(hideHeadRow)` encodes [peer A, nested peer-pair] as [secA, composite{sec,sec…}].
         * Classic parse (head + inner.map) flattens that into one branch head + loose leaves, which
         * round-trips with fewer nested nodes and breaks split/add paths (see debug H5 wireLeaves drop).
         */
        if (innerAllBareSecurities) {
            const headSec = parts[0].securityAlertCondition!
            return {
                security: { ...emptySecurityAlertCondition },
                compositeUi: { hideHeadRow: true },
                parentToGroupOp: op,
                childrenSiblingOp: op,
                children: [leafNode(headSec), parseBranchFromCondition({ compositeCondition: inner })]
            }
        }
        /**
         * Splitting a leaf inside a nested `hideHeadRow` group yields an inner operand list like
         * `[composite{pair}, security, …]` (mixed). Wire is still `[secPeer, composite{inner}]`.
         * Classic `head + inner.map()` would lift every inner operand to sibling rows under one head,
         * flattening the nested group (wrong tree and layout after save).
         */
        if (innerParts.length >= 2) {
            const headSec = parts[0].securityAlertCondition!
            return {
                security: { ...emptySecurityAlertCondition },
                compositeUi: { hideHeadRow: true },
                parentToGroupOp: op,
                childrenSiblingOp: op,
                children: [leafNode(headSec), parseBranchFromCondition({ compositeCondition: inner })]
            }
        }
        const headSec = parts[0].securityAlertCondition!
        const base: CompositeBranchNode = {
            security: headSec,
            parentToGroupOp: op,
            childrenSiblingOp: inner.logicalOperator || defaultOp(),
            children: (inner.condition || []).map(parseBranchFromCondition)
        }
        if (isPlaceholderHeaderSecurity(headSec)) {
            return { ...base, compositeUi: { hideHeadRow: true } }
        }
        return base
    }

    /**
     * Flat `hideHeadRow` operand list: any mix of composites and/or securities except **all** bare
     * securities (handled below). Includes all-composite peers (`[subgroup, subgroup, …]`), which
     * otherwise fell through to `parseBranchFromCondition(parts[0])` and dropped siblings (H5: 5→3, 6→4).
     * Classic `[sec, composite(inner)]` is handled above. Top-level multi-root `[unary, unary]` is only
     * parsed via {@link conditionToForestModel}'s `parts.map`, not as one call here.
     */
    if (parts.length >= 2 && !parts.every((p) => p.securityAlertCondition)) {
        return {
            security: { ...emptySecurityAlertCondition },
            compositeUi: { hideHeadRow: true },
            parentToGroupOp: op,
            childrenSiblingOp: op,
            children: parts.map(parseBranchFromCondition)
        }
    }

    /**
     * Flat `composite { sec, sec, … }` is how {@link branchToCondition} encodes `hideHeadRow` peer lists
     * (any count ≥ 2). Parsing 3+ as “head + rest” or as separate roots breaks the tree after repeated splits.
     */
    if (parts.length > 0 && parts.every((p) => p.securityAlertCondition)) {
        if (parts.length === 1) {
            return leafNode(parts[0].securityAlertCondition!)
        }
        return {
            security: { ...emptySecurityAlertCondition },
            compositeUi: { hideHeadRow: true },
            parentToGroupOp: op,
            childrenSiblingOp: op,
            children: parts.map((p) => leafNode(p.securityAlertCondition!))
        }
    }

    if (parts[0]) {
        return parseBranchFromCondition(parts[0])
    }
    const primary = getPrimarySecurityAlertCondition(cond)
    if (primary) return leafNode(primary)
    return leafNode(emptySecurityAlertCondition)
}

function syncGroupOpsInTree(n: CompositeBranchNode): CompositeBranchNode {
    if (n.children.length === 0) return n
    const op = n.parentToGroupOp
    return {
        ...n,
        parentToGroupOp: op,
        childrenSiblingOp: op,
        children: n.children.map(syncGroupOpsInTree)
    }
}

function syncForestRoots(roots: CompositeBranchNode[]): CompositeBranchNode[] {
    return roots.map(syncGroupOpsInTree)
}

export function conditionToForestModel(condition: Condition | undefined): ConditionForestModel {
    if (!condition) {
        return { rootSiblingOp: defaultOp(), roots: [] }
    }
    if (condition.securityAlertCondition) {
        return { rootSiblingOp: defaultOp(), roots: [leafNode(condition.securityAlertCondition)] }
    }
    const comp = condition.compositeCondition
    if (!comp?.condition?.length) {
        return { rootSiblingOp: defaultOp(), roots: [] }
    }
    const parts = comp.condition
    const op = comp.logicalOperator || defaultOp()
    if (
        parts.length === 2 &&
        parts[0].securityAlertCondition &&
        parts[1].compositeCondition?.condition?.length
    ) {
        return {
            rootSiblingOp: op,
            roots: syncForestRoots([parseBranchFromCondition(condition)])
        }
    }
    /** Top-level `composite { sec, sec, … }` is one `hideHeadRow` group (not N separate roots). */
    if (parts.length >= 2 && parts.every((p) => p.securityAlertCondition)) {
        return {
            rootSiblingOp: op,
            roots: syncForestRoots([parseBranchFromCondition(condition)])
        }
    }
    /**
     * `hideHeadRow` peers serialize as a flat `condition` list. After splitting the first peer, the list is
     * `[ composite(inner…), security, … ]` — not the multi-root pattern (every operand wrapped in a unary
     * composite). Without this branch, `parts.map` becomes multiple roots: rail paths break and split/sibling
     * intents target the wrong nodes.
     */
    if (
        parts.length >= 2 &&
        parts.some((p) => p.securityAlertCondition) &&
        parts.some((p) => !p.securityAlertCondition)
    ) {
        return {
            rootSiblingOp: op,
            roots: syncForestRoots([
                {
                    security: { ...emptySecurityAlertCondition },
                    compositeUi: { hideHeadRow: true },
                    parentToGroupOp: op,
                    childrenSiblingOp: op,
                    children: parts.map(parseBranchFromCondition)
                }
            ])
        }
    }
    return {
        rootSiblingOp: op,
        roots: syncForestRoots(parts.map(parseBranchFromCondition))
    }
}

function cloneNode(n: CompositeBranchNode): CompositeBranchNode {
    const { children, ...rest } = n
    return {
        ...rest,
        children: children.map(cloneNode)
    }
}

function cloneForest(m: ConditionForestModel): ConditionForestModel {
    return {
        rootSiblingOp: m.rootSiblingOp,
        roots: m.roots.map(cloneNode)
    }
}

function liftNodeByRemovingHeader(n: CompositeBranchNode): CompositeBranchNode {
    const { children } = n
    if (children.length === 0) {
        return cloneNode(n)
    }
    if (children.length === 1) {
        return cloneNode(children[0])
    }
    const first = children[0]
    const rest = children.slice(1).map(cloneNode)
    if (first.children.length === 0) {
        const head = cloneNode(first)
        return {
            ...head,
            parentToGroupOp: n.parentToGroupOp,
            childrenSiblingOp: n.childrenSiblingOp,
            children: rest
        }
    }
    return {
        security: { ...first.security },
        parentToGroupOp: n.parentToGroupOp,
        childrenSiblingOp: n.childrenSiblingOp,
        children: [...first.children.map(cloneNode), ...rest]
    }
}

export function getNodeAtPath(model: ConditionForestModel, path: number[]): CompositeBranchNode | undefined {
    if (path.length === 0) return undefined
    let cur: CompositeBranchNode | undefined = model.roots[path[0]]
    if (!cur) return undefined
    for (let i = 1; i < path.length; i++) {
        cur = cur.children[path[i]]
        if (!cur) return undefined
    }
    return cur
}

export function updateSecurityAtPath(
    model: ConditionForestModel,
    path: number[],
    security: Condition_SecurityAlertCondition
): ConditionForestModel {
    const next = cloneForest(model)
    const node = getNodeAtPath(next, path)
    if (!node) return model
    node.security = { ...security }
    return next
}

/** Path to the leftmost leaf (matches {@link getPrimarySecurityAlertCondition} DFS order for typical trees). */
export function getPrimaryEditableLeafPath(model: ConditionForestModel): number[] | null {
    if (model.roots.length === 0) return null
    return leftmostLeafPathFrom([0], model.roots[0])
}

/**
 * Apply a new primary security row while preserving composite shape.
 * Fixes single-row editor `dispatch(setUserAlertCondition)` no-op when the root is `compositeCondition`.
 */
export function replacePrimarySecurityAlertCondition(
    condition: Condition | undefined,
    security: Condition_SecurityAlertCondition
): Condition {
    if (!condition) {
        return { securityAlertCondition: security }
    }
    if (condition.securityAlertCondition) {
        return {
            ...condition,
            securityAlertCondition: security
        }
    }
    if (!condition.compositeCondition?.condition?.length) {
        return { securityAlertCondition: security }
    }
    const forest = conditionToForestModel(condition)
    const path = getPrimaryEditableLeafPath(forest)
    if (!path) {
        return { securityAlertCondition: security }
    }
    const nextForest = updateSecurityAtPath(forest, path, security)
    const nextCond = forestModelToCondition(nextForest)
    return nextCond ?? { securityAlertCondition: security }
}

/**
 * Split target node into a nested AND/OR pair: the existing condition becomes the **first** row inside
 * a new inner group, and `newSecurity` is the second. The previous header row is removed (structural
 * placeholder + `compositeUi.hideHeadRow`). Used for rail/inline split on a leaf, and for split on the
 * branch header row (existing children stay as siblings after the new inner block).
 */
export function splitIntoNestedPairAtPath(
    model: ConditionForestModel,
    path: number[],
    newSecurity: Condition_SecurityAlertCondition
): ConditionForestModel {
    const next = cloneForest(model)
    const node = getNodeAtPath(next, path)
    if (!node) {
        return model
    }

    const carried = { ...node.security }
    const { parentToGroupOp, childrenSiblingOp } = node

    if (node.children.length === 0) {
        node.security = { ...emptySecurityAlertCondition }
        node.compositeUi = { hideHeadRow: true }
        node.parentToGroupOp = parentToGroupOp
        node.childrenSiblingOp = childrenSiblingOp
        node.children = [leafNode(carried), leafNode(newSecurity)]
        return next
    }

    /**
     * `hideHeadRow` groups only have a structural placeholder as `security`. The branch-header split below
     * would carry that template as a fake "first" row. If split is requested on the group path (e.g. `[0]`
     * instead of `[0,0]`), delegate to the first visible leaf so the first row still splits correctly.
     */
    if (
        node.compositeUi?.hideHeadRow === true &&
        isPlaceholderHeaderSecurity(node.security) &&
        node.children[0]
    ) {
        const leafPath = leftmostLeafPathFrom([...path, 0], node.children[0])
        if (leafPath) {
            return splitIntoNestedPairAtPath(next, leafPath, newSecurity)
        }
        return model
    }

    const existing = node.children.map(cloneNode)
    const subgroup: CompositeBranchNode = {
        security: { ...emptySecurityAlertCondition },
        compositeUi: { hideHeadRow: true },
        parentToGroupOp,
        childrenSiblingOp,
        children: [leafNode(carried), leafNode(newSecurity)]
    }
    node.security = { ...emptySecurityAlertCondition }
    node.compositeUi = { hideHeadRow: true }
    node.parentToGroupOp = parentToGroupOp
    node.childrenSiblingOp = childrenSiblingOp
    node.children = [subgroup, ...existing]
    return next
}

export function addChildLeafAtPath(
    model: ConditionForestModel,
    path: number[],
    security: Condition_SecurityAlertCondition
): ConditionForestModel {
    const peek = getNodeAtPath(model, path)
    if (!peek) return model
    if (peek.children.length === 0) {
        return splitIntoNestedPairAtPath(model, path, security)
    }
    const next = cloneForest(model)
    const node = getNodeAtPath(next, path)!
    node.children.push(leafNode(security))
    return next
}

/**
 * Add a new condition as a **peer in the same AND/OR group** as the node at `path`.
 * - If that node is a branch (`children.length > 0`), appends a new leaf row to its child list
 *   (correct for the tree footer `+` on a group — {@link addSiblingLeafAfterPath} on a group path
 *   would wrongly insert a new **top-level root** instead).
 * - If it is a leaf, inserts after it under the same parent ({@link addSiblingLeafAfterPath}).
 */
export function addPeerLeafInGroupAtPath(
    model: ConditionForestModel,
    path: number[],
    security: Condition_SecurityAlertCondition
): ConditionForestModel {
    if (path.length === 0) return model
    const next = cloneForest(model)
    const node = getNodeAtPath(next, path)
    if (!node) return model
    if (node.children.length > 0) {
        node.children.push(leafNode(security))
        return next
    }
    return addSiblingLeafAfterPath(model, path, security)
}

/**
 * Insert a new leaf **after** the node at `path` (same parent list: root row or `parent.children`).
 * For a **group** node path (branch with children), prefer {@link addPeerLeafInGroupAtPath} so the new
 * row stays inside that group.
 */
export function addSiblingLeafAfterPath(
    model: ConditionForestModel,
    path: number[],
    security: Condition_SecurityAlertCondition
): ConditionForestModel {
    if (path.length === 0) return model
    const next = cloneForest(model)
    const lastIdx = path[path.length - 1]
    const insertAt = lastIdx + 1

    if (path.length === 1) {
        if (lastIdx < 0 || lastIdx >= next.roots.length) return model
        const hadOneRoot = next.roots.length === 1
        next.roots.splice(insertAt, 0, leafNode(security))
        if (hadOneRoot && next.roots[0].children.length > 0) {
            next.rootSiblingOp = next.roots[0].parentToGroupOp
            next.roots[0].childrenSiblingOp = next.rootSiblingOp
        }
        return next
    }

    const parentPath = path.slice(0, -1)
    const parent = getNodeAtPath(next, parentPath)
    if (!parent || lastIdx < 0 || lastIdx >= parent.children.length) return model
    parent.children.splice(insertAt, 0, leafNode(security))
    return next
}

export function addRootLeaf(model: ConditionForestModel, security: Condition_SecurityAlertCondition): ConditionForestModel {
    const next = cloneForest(model)
    const hadOneRoot = next.roots.length === 1
    next.roots.push(leafNode(security))
    if (hadOneRoot && next.roots[0].children.length > 0) {
        next.rootSiblingOp = next.roots[0].parentToGroupOp
        next.roots[0].childrenSiblingOp = next.rootSiblingOp
    }
    return next
}

function collapseEmptyCompositeChain(model: ConditionForestModel, pathToNode: number[]): ConditionForestModel {
    let path = [...pathToNode]
    let iterations = 0
    while (path.length > 0 && iterations++ < 64) {
        const node = getNodeAtPath(model, path)
        if (!node || node.children.length > 0) break
        const asLeaf = leafNode(node.security)
        if (path.length === 1) {
            model.roots[path[0]] = asLeaf
        } else {
            const gp = getNodeAtPath(model, path.slice(0, -1))
            if (!gp) break
            gp.children[path[path.length - 1]] = asLeaf
        }
        path = path.slice(0, -1)
    }
    return model
}

export function removeNodeAtPath(model: ConditionForestModel, path: number[]): ConditionForestModel {
    if (path.length === 0) return model
    const next = cloneForest(model)
    if (path.length === 1) {
        const idx = path[0]
        const node = next.roots[idx]
        if (!node) return model
        if (node.children.length > 0) {
            next.roots[idx] = liftNodeByRemovingHeader(node)
            return collapseEmptyCompositeChain(next, [idx])
        }
        next.roots.splice(idx, 1)
        return next
    }
    const parentPath = path.slice(0, -1)
    const idx = path[path.length - 1]
    const parent = getNodeAtPath(next, parentPath)
    if (!parent || idx < 0 || idx >= parent.children.length) return model
    const target = parent.children[idx]
    if (target.children.length > 0) {
        parent.children[idx] = liftNodeByRemovingHeader(target)
        return collapseEmptyCompositeChain(next, path)
    }
    parent.children.splice(idx, 1)
    return collapseEmptyCompositeChain(next, parentPath)
}

export function toggleRootSiblingOp(model: ConditionForestModel): ConditionForestModel {
    const next = cloneForest(model)
    next.rootSiblingOp = flipOp(next.rootSiblingOp)
    return next
}

export function toggleNodeParentToGroupOp(model: ConditionForestModel, path: number[]): ConditionForestModel {
    const next = cloneForest(model)
    const node = getNodeAtPath(next, path)
    if (!node) return model
    node.parentToGroupOp = flipOp(node.parentToGroupOp)
    if (node.children.length > 0) {
        node.childrenSiblingOp = node.parentToGroupOp
    }
    return next
}

/** Deepest-only leaves (ignores the header row when a branch has a child group). */
export function countLeavesInForest(model: ConditionForestModel): number {
    let n = 0
    const walk = (node: CompositeBranchNode) => {
        if (node.children.length === 0) n += 1
        else node.children.forEach(walk)
    }
    model.roots.forEach(walk)
    return n
}

/** Every condition row in the UI: one for the branch header if it has children, plus nested rows. */
export function countConditionRowsInForest(model: ConditionForestModel): number {
    const countNode = (node: CompositeBranchNode): number => {
        if (node.children.length === 0) return 1
        return 1 + node.children.reduce((acc, ch) => acc + countNode(ch), 0)
    }
    return model.roots.reduce((acc, r) => acc + countNode(r), 0)
}

export function countLeafChildrenUnder(node: CompositeBranchNode): number {
    if (node.children.length === 0) return 0
    let n = 0
    const walk = (nd: CompositeBranchNode) => {
        if (nd.children.length === 0) n += 1
        else nd.children.forEach(walk)
    }
    node.children.forEach(walk)
    return n
}
